import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
  PaddingProps,
  SizeProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  height,
  justifyContent,
  padding,
  size,
  width,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import type { CursorProps } from '../../theme/system-utilities';
import { cursor, fill } from '../../theme/system-utilities';
import { Flex } from '../Flex';
import Icon from '../Icon';

const CONTAINER_SIZE = 42;
const SEARCH_ICON_SIZE = 24;
const MINIMAL_SEARCH_ICON_SIZE = 13;

interface SearchProps {
  onClick: () => void;
  withAnimation?: boolean;
  minimalIcon?: boolean;
}

const StyledIcon = styled(Icon)<any>`
  ${fill};
  ${size};
`;

const iconStyles: Partial<ComponentProps<typeof StyledIcon>> = {
  fill: 'sys.neutral.icon.default',
  role: 'presentation',
  size: SEARCH_ICON_SIZE,
};

const Container = styled(Flex)<CursorProps & SizeProps>`
  ${cursor};
  ${size};
`;

const containerStyles: Partial<ComponentProps<typeof Container>> = {
  alignItems: 'center',
  cursor: 'pointer',
  justifyContent: 'center',
  size: CONTAINER_SIZE,
};

const AnimatedContainer = styled.div<
  PaddingProps &
    BorderRadiusProps &
    BorderWidthProps &
    BorderColorProps &
    BorderStyleProps &
    DisplayProps &
    WidthProps &
    CursorProps &
    AlignItemsProps &
    JustifyContentProps &
    HeightProps
>`
  ${padding};
  ${borderRadius};
  ${borderWidth};
  ${borderColor};
  ${borderStyle};
  ${display};
  ${width};
  ${cursor};
  ${alignItems};
  ${justifyContent};
  ${width};
  ${height};

  ${() => css`
    @keyframes shrinkSearchWidth {
      from {
        width: 100%;
        margin-top: 16px;
        padding-right: 12px;
      }
      to {
        width: 24px;
        margin-top: 0px;
        justify-content: center;
      }
    }

    animation: shrinkSearchWidth 0.5s ease-in-out forwards;
    float: right;
  `}
`;

const animatedContainerStyles: Partial<ComponentProps<typeof AnimatedContainer>> = {
  padding: 'sp_8',
  borderRadius: '100px',
  borderWidth: 'small',
  borderColor: 'sys.neutral.border.default',
  borderStyle: 'solid',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: '100%',
  cursor: 'pointer',
  height: '24px',
};

const SearchIcon = ({ onClick, withAnimation = false, minimalIcon = false }: SearchProps) => {
  const { t } = useLocalization();

  const IconContainer = withAnimation ? AnimatedContainer : Container;
  const iconContainerStyles = withAnimation ? animatedContainerStyles : containerStyles;
  const iconSize = minimalIcon ? MINIMAL_SEARCH_ICON_SIZE : SEARCH_ICON_SIZE;
  const iconName = minimalIcon ? 'searchFillSmall' : 'search';

  return (
    <IconContainer
      onClick={onClick}
      data-testid="search-icon-container"
      aria-label={t('h24_search_button_text')}
      role="button"
      {...iconContainerStyles}
    >
      <StyledIcon name={iconName} {...iconStyles} size={iconSize} />
    </IconContainer>
  );
};

export default SearchIcon;
